import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { postBillingKey, postSubscription } from "src/api/subscription";
import { BILLING_TERMS_VERSION } from "../constants/billing";
import { buildSubscriptionCompleteUrl } from "../utils/subscriptionCompleteParams";
import { parseBillingFailParams, parseBillingSuccessParams } from "./usePaymentUtils";
/** 처리 후 URL에서 걷어낼 빌링 returnURL 파라미터 */ const BILLING_RETURN_PARAM_KEYS = [
    "billingResult",
    "authKey",
    "customerKey",
    "code",
    "message"
];
/** 첫 회차 결제 실패. 구독은 pending으로 남아 재시도 시 재사용된다. */ const PAYMENT_REQUIRED = 402;
/**
 * 월구독 빌링 등록 결제창에서 돌아왔을 때의 returnURL 처리
 *
 * 일회용 결제의 returnURL 처리(usePayment)와 같은 자리지만, 빌링은 결제창이 아니라
 * 등록창을 다녀온 것이라 복귀 시점에는 아직 결제도 가입도 되어 있지 않다. 여기서 두 번 호출한다.
 *
 *   authKey → POST /billing-keys → billingKeyId → POST /subscriptions → 가입 + 첫 회차 결제
 *
 * 일회용 결제와 달리 승인 후 별도 confirm이 없다. `POST /subscriptions`가 결제까지 끝낸다.
 */ export const useBillingReturn = (param)=>{
    let { enabled , planId , planTitle , onError  } = param;
    const router = useRouter();
    // router.query가 여러 번 갱신되므로 결과 처리는 1회로 제한한다.
    // authKey는 1회용이라 두 번 보내면 두 번째가 실패한다.
    const handledRef = useRef(false);
    // 실패 문구를 단계별로 나누기 위한 표시. 가입 요청까지 갔는지만 알면 된다.
    const subscribeRequestedRef = useRef(false);
    /**
   * 빌링키 발급 → 가입을 한 뮤테이션으로 묶는다.
   * 둘 사이에 유저 상호작용이 없고, 중간에서 끊기면 어느 쪽이든 '카드만 등록되고 미가입' 상태라
   * 실패 처리도 하나로 묶는 편이 맞다.
   */ const subscribeMutation = useMutation({
        mutationFn: async (param)=>{
            let { authKey  } = param;
            subscribeRequestedRef.current = false;
            const { billingKeyId  } = await postBillingKey({
                authKey
            });
            subscribeRequestedRef.current = true;
            return postSubscription({
                planId,
                billingKeyId,
                // 미동의면 결제 CTA에서 막혀 등록창까지 오지 못한다
                termsAgreed: true,
                termsVersion: BILLING_TERMS_VERSION
            });
        },
        onSuccess: (result)=>{
            var _result_paidResult;
            var _result_paidResult_paidAmount;
            // MX-4 — 가입이 확정된 시점에 쏜다.
            // 완료 페이지가 아니라 여기인 이유: 완료 화면은 쿼리스트링만 받아 그리므로 상품 정보·구독 id 가 없고,
            // 새로고침·뒤로가기로 재진입하면 같은 결제가 중복 집계된다.
            mixpanelEvent("Purchase Completed", {
                "Product Categories": "membership",
                "Product IDs": planId,
                "Product Names": planTitle,
                "Order Total": (_result_paidResult_paidAmount = (_result_paidResult = result.paidResult) === null || _result_paidResult === void 0 ? void 0 : _result_paidResult.paidAmount) !== null && _result_paidResult_paidAmount !== void 0 ? _result_paidResult_paidAmount : 0,
                "# of Products": 1,
                "Order Type": "membership",
                "Is Free Trial": result.status === "trial",
                // TODO(지표): 자동 갱신·복구 결제는 유저 행동이 아니라 서버에서 일어난다.
                // 이 경로는 신규 가입 전용이라 항상 false 이며, 두 케이스는 BE 이벤트로 받아야 한다.
                "Is Renewal": false,
                "Is Payment Retry": false,
                "Membership Id": result.subscriptionId
            });
            // 완료 화면은 주문을 조회하지 않고 이 응답값을 쿼리로 받아 그린다 (체험은 orderId가 없다)
            router.replace(buildSubscriptionCompleteUrl(result));
        },
        onError: (error)=>{
            const phase = subscribeRequestedRef.current ? "subscribe" : "billing-key";
            Sentry.captureException(error, {
                tags: {
                    feature: "monthly-billing",
                    phase
                },
                fingerprint: [
                    "monthly-billing-".concat(phase)
                ]
            });
            // 실패하면 같은 화면에 남는다. 재시도는 결제 CTA(등록창)부터 다시 탄다.
            onError === null || onError === void 0 ? void 0 : onError({
                message: getSubscribeErrorMessage(error, subscribeRequestedRef.current),
                type: "payment_error"
            });
        }
    });
    useEffect(()=>{
        if (!enabled || !router.isReady || handledRef.current) return;
        if (router.query.billingResult === undefined) return;
        const successParams = parseBillingSuccessParams(router.query);
        const failParams = parseBillingFailParams(router.query);
        if (!successParams && !failParams) return;
        handledRef.current = true;
        // 처리 대상을 확보했으면 즉시 지운다. authKey는 이미 successParams에 담겨 있어 요청에는 영향이 없고,
        // 요청이 도는 중에 새로고침되면 handledRef가 초기화되어 소진된 authKey로 다시 호출되는 것을 막는다.
        clearBillingParams();
        if (successParams) {
            handleBillingSuccess(successParams);
        }
        if (failParams) {
            handleBillingFail(failParams);
        }
    }, [
        enabled,
        router.isReady,
        router.query
    ]);
    /** 등록창 통과. 아직 가입도 결제도 되지 않았으므로 이어서 서버 호출을 태운다. */ const handleBillingSuccess = (params)=>{
        subscribeMutation.mutate(params);
    };
    // 등록창에서 실패하거나 유저가 취소한 경우. 서버 호출까지 가지 않는다.
    const handleBillingFail = (param)=>{
        let { message  } = param;
        onError === null || onError === void 0 ? void 0 : onError({
            message: "정기결제 등록에 실패했습니다: ".concat(message),
            type: "payment_error"
        });
    };
    /**
   * 처리한 빌링 파라미터를 URL에서 제거한다.
   *
   * 두 가지를 막는다.
   *  1. 재시도 시 이전 결과의 code·message가 새 successUrl에 섞여 들어가는 것
   *     (successUrl/failUrl을 현재 URL 기준으로 만든다 — createUrlWithParams)
   *  2. 요청이 도는 중 새로고침되어 소진된 authKey가 다시 전송되는 것
   *
   * id·orderType은 화면 유지에 필요하므로 남긴다.
   *
   * router.replace 대신 history.replaceState를 쓴다. 주소창만 바꾸면 되는데
   * 라우터를 거치면 리렌더·데이터 페칭 등 다른 동작이 끼어들 여지가 생긴다.
   * createUrlWithParams가 window.location.href를 읽으므로 이것만으로 목적은 달성된다.
   */ const clearBillingParams = ()=>{
        const url = new URL(window.location.href);
        BILLING_RETURN_PARAM_KEYS.forEach((key)=>url.searchParams.delete(key));
        window.history.replaceState(window.history.state, "", url.toString());
    };
    return {
        /** 빌링키 발급 + 가입 진행 중. 이 사이 화면은 결제 대기 상태다 */ isSubscribing: subscribeMutation.isLoading
    };
};
/**
 * 실패 원인별 안내 문구.
 *
 * 서버 message를 그대로 노출하지 않는 이유는 axios 기본 문구('Request failed with status code 500')가
 * 그대로 스낵바에 뜨기 때문이다.
 *
 * @param subscribeRequested 가입 요청(`POST /subscriptions`)까지 갔는지
 */ export const getSubscribeErrorMessage = (error, subscribeRequested)=>{
    var _error_response;
    // 빌링키 발급 단계에서 끊긴 경우. 가입 요청이 나가지 않았으므로 재시도만 안내하면 된다.
    // 응답이 유실돼 서버에서는 발급됐더라도 billingKeyId를 알 수 없어 어차피 새로 등록해야 한다.
    if (!subscribeRequested) {
        return "카드 등록에 실패했어요. 다시 시도해주세요.";
    }
    if (isAxiosError(error) && ((_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status) === PAYMENT_REQUIRED) {
        return "결제에 실패했어요. 카드 상태를 확인한 뒤 다시 시도해주세요.";
    }
    // 응답이 없다 = 서버가 뭐라고 답했는지 모른다. 가입이 성공했을 수 있으므로 재시도를 권하지 않는다.
    // 틀렸을 때의 대가가 비대칭이다 — 확인 안내가 틀리면 한 번 더 누르면 되지만,
    // 재시도 안내가 틀리면 카드가 두 번 긁힌다.
    if (isAxiosError(error) && !error.response) {
        return "구독 신청 결과를 확인하지 못했어요. 구독 관리에서 가입 여부를 확인해주세요.";
    }
    return "구독 신청에 실패했어요. 잠시 후 다시 시도해주세요.";
};
